import * as cancerScreeningService from "../../services/cancerScreeningService";
import * as localStorageKeys from "../../constants/localStorageKeys";

import { Grid, useTheme } from "@material-ui/core";
import NewScreeningRequestsFilter, {
  IFormValues,
} from "../../components/CancerScreening/NewScreeningRequestsFilter";
import React, { useContext, useEffect, useState } from "react";
import moment, { Moment } from "moment";

import { CancerScreeningResultModel } from "../../models/cancerScreeningResultModel";
import { LangContext } from "../../context/LangContext";
import Layout from "../../components/Layout";
import LoadingIndicator from "../../components/LoadingIndicator";
import ScreeningRequestsOverviewList from "../../components/CancerScreening/ScreeningRequestsOverviewList";
import { ScreeningStatus } from "../../models/getCancerScreeningsModel";
import Section from "../../components/Section";
import { TABLE_ROWS_DEFAULT } from "../../constants";
import { clearSavedNewScreeningRequest } from "../../utils";
import translations from "../../assets/json/translations.json";

const NewScreeningRequestsPage: React.FC = () => {
  const theme = useTheme();
  const [lang] = useContext(LangContext);


  const [loadingScreeningRequests, setloadingScreeningRequests] =
    useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_ROWS_DEFAULT);
  const [count, setCount] = useState(0);
  const [allScreeningRequests, setAllScreeningRequests] = useState<
    CancerScreeningResultModel[] | null
  >(null);
  const [loadForm, setLoadForm] = useState<IFormValues | null>(null);
  const [formCleared, setFormCleared] = useState(false);

  const initialForm: IFormValues = {
    patientSsn: "",
    requestorSsn: "",
    examinationDateFrom: moment().subtract(6, "months").startOf('day') as Moment | null,
    examinationDateTo: moment().endOf('day') as Moment | null,
  };

  useEffect(() => {
    const form = localStorage.getItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_FILTER_FORM}`
    );

    const pageSize = localStorage.getItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_PAGE_SIZE}`
    );

    if (pageSize) {
      setRowsPerPage(Number(pageSize));
    }

    if (form) {
      const formJson = JSON.parse(form);

      const formValues: IFormValues = {
        patientSsn: formJson?.patientSsn || "",
        requestorSsn: formJson?.requestorSsn || "",
        examinationDateFrom: formJson?.examinationDateFrom
          ? (moment(formJson.examinationDateFrom) as Moment | null)
          : (null as Moment | null),
        examinationDateTo: formJson?.examinationDateTo
          ? (moment(formJson.examinationDateTo) as Moment | null)
          : (null as Moment | null),
      };

      setLoadForm(formValues);
    } else {
      setLoadForm({ ...initialForm });
    }
    /* eslint-disable */
  }, []);
  /* eslint-enable */

  useEffect(() => {
    if (!initialLoad) {
      // save rowsPerPage in localStorage everytime rowsPerPage changes
      localStorage.setItem(
        `${localStorageKeys.NEW_SCREENING_REQUESTS_PAGE_SIZE}`,
        JSON.stringify(rowsPerPage)
      );
    }
    /* eslint-disable */
  }, [rowsPerPage]);
  /* eslint-enable */

  return (
    <Layout useDefaultSpacing title={translations.screeningRequestsOverview[lang]}>
      <Section>
        {loadForm && (
          <NewScreeningRequestsFilter
            onSubmit={getNewScreeningRequests}
            onClear={() => {
              setFormCleared(true);
              setAllScreeningRequests(null);
              setCurrentPage(0);
              clearForm();
              clearSavedNewScreeningRequest();
            }}
            loadForm={loadForm || undefined}
            formCleared={formCleared}
          />
        )}

        {loadingScreeningRequests ? (
          <Grid
            container
            justify="center"
            alignItems="center"
            style={{ marginTop: theme.spacing(2) }}
          >
            <LoadingIndicator />
          </Grid>
        ) : (
          allScreeningRequests && (
            <ScreeningRequestsOverviewList
              screeningRequests={allScreeningRequests}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              count={count}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
            />
          )
        )}
      </Section>
    </Layout>
  );

  async function getNewScreeningRequests(
    values: IFormValues,
    initialLoad?: boolean
  ) {
    setloadingScreeningRequests(true);
    if (!initialLoad) {
      localStorage.setItem(
        `${localStorageKeys.NEW_SCREENING_REQUESTS_FILTER_FORM}`,
        JSON.stringify(values)
      );
    }

    const response = await cancerScreeningService.getCancerScreenings({
      requestorSsn: values?.requestorSsn,
      requestorName: "",
      personSsn: values?.patientSsn,
      personName: "",
      dateTimeFrom: values?.examinationDateFrom
        ? new Date(values?.examinationDateFrom.toISOString())
        : undefined,
      dateTimeTo: values?.examinationDateTo
        ? new Date(values.examinationDateTo.toISOString())
        : undefined,
      screeningStatus: [ScreeningStatus.Created, ScreeningStatus.Completed, ScreeningStatus.Canceled, ScreeningStatus.SentToSkrin],
    });

    if (response.isOk) {
      setAllScreeningRequests(response?.data?.cancerScreenings || []);

      setCount(response?.data?.cancerScreenings?.length || 0);
      setloadingScreeningRequests(false);
      setFormCleared(false);
      setCurrentPage(0);
      if (initialLoad) {
        setInitialLoad(false);
        setLoadForm(values);
      }
    } else {
      setloadingScreeningRequests(false);
      setAllScreeningRequests(null);
    }
  }

  function clearForm() {
    setLoadForm(initialForm);
  }
};

export default NewScreeningRequestsPage;
